import { paths } from 'src/routes/paths';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/context/organization-context';
import { Outlet, useNavigate, useLocation, createFileRoute } from '@tanstack/react-router';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/settings/client/brand')({
  component: ClientSettingsLayout,
});


function ClientSettingsLayout() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { orgId } = Route.useParams();
  const { isOwner } = useOrganization();
  const isUserOwner = isOwner();

  // Tenant-prefixed brand-settings tabs. The org lives in the URL, so each tab
  // target carries its `/o/:org` prefix; navigating to the bare flat path would
  // round-trip through the 404 forwarder.
  const clientPaths = paths.dashboard.orgSettings(orgId).client;

  const SECTIONS = [
    { label: t('brand.tabs.manage', 'Manage'), value: clientPaths.brand, ownerOnly: true },
    { label: t('brand.tabs.brandProfile', 'Brand Profile'), value: clientPaths.brandProfiles },
    { label: t('brand.tabs.integrations', 'Integrations'), value: clientPaths.brandIntegrations },
  ] as const;

  const visibleSections = SECTIONS.filter((s) => !('ownerOnly' in s && s.ownerOnly) || isUserOwner);

  const visibleValues = new Set(visibleSections.map((s) => s.value));

  // Match active tab: check if pathname starts with a section value
  // For detail pages like /brand/cl_xxx, highlight Brand Profile
  // For /brand/cl_xxx/integrations/shopify, highlight Integrations
  const activeValue = (() => {
    if (pathname.includes('/integrations')) {
      return clientPaths.brandIntegrations;
    }
    if (pathname === clientPaths.brand || pathname === `${clientPaths.brand}/`) {
      // If the Manage tab is visible, use it; otherwise fall back to first visible tab
      if (visibleValues.has(clientPaths.brand as typeof visibleSections[number]['value'])) {
        return clientPaths.brand;
      }
      return visibleSections[0].value;
    }
    if (pathname.startsWith(clientPaths.brandProfiles) || pathname.match(/\/brand\/[^/]+$/)) {
      return clientPaths.brandProfiles;
    }
    return visibleSections[0].value;
  })();

  return (
    <Box>
      <SectionTabs
        value={activeValue}
        onChange={(_, v) => navigate({ to: v })}
      >
        {visibleSections.map((s) => (
          <SectionTab key={s.value} label={s.label} value={s.value} disableRipple />
        ))}
      </SectionTabs>

      <Box sx={{ mt: 3 }}>
        <Outlet />
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

const SectionTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 36,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    height: 2,
    borderRadius: '2px 2px 0 0',
  },
}));

const SectionTab = styled(Tab)(({ theme }) => ({
  minHeight: 36,
  textTransform: 'none',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.75, 1.5),
  minWidth: 'auto',
  '&.Mui-selected': {
    fontWeight: 600,
  },
}));
